import { z } from 'zod';
import type { IsoDate } from './dates';
import type { ReportStatus } from './enums';
import { REPORT_STATUSES } from './enums';
import { idSchema, isoDateSchema, listParamsSchema, optionalText } from './schemas';
import { isActiveOn } from './staffing';

// ───────────── Правило 12.4.4: перевірки звіту про послугу ─────────────

export type ReportViolation =
  | 'future_date'
  | 'outside_contract'
  | 'outside_assignment'
  | 'service_not_allowed'
  | 'absence_day'
  | 'exceeds_timesheet'
  | 'location_outside_area'
  | 'location_missing';

/** Порушення, з якими звіт не можна подати. Решта — попередження для погоджувачів. */
export const BLOCKING_VIOLATIONS: ReportViolation[] = ['future_date', 'outside_contract'];

export interface ReportCheckInput {
  date: IsoDate;
  serviceId: string;
  hours: number;
  katottgCode: string | null;
}

export interface ReportCheckContext {
  today: IsoDate;
  /** Послуги, дозволені штатній одиниці звіту. */
  allowedServiceIds: string[];
  assignment: { startsAt: IsoDate; endsAt: IsoDate | null } | null;
  contract: { startsAt: IsoDate; endsAt: IsoDate | null } | null;
  /** Години в табелі за цей день (null — табель не заповнено). */
  timesheetHours: number | null;
  /** День позначено відсутністю в табелі. */
  absence: boolean;
  /** Сума годин інших звітів працівника за цей день. */
  otherReportsHours: number;
  /** Предки місця надання послуги в КАТОТТГ (включно з ним). */
  locationPath: string[] | null;
  /** Зона роботи центру або проєкту (коди КАТОТТГ); порожньо — без обмежень. */
  areaCodes: string[];
}

export function checkServiceReport(
  input: ReportCheckInput,
  ctx: ReportCheckContext,
): ReportViolation[] {
  const v: ReportViolation[] = [];
  if (input.date > ctx.today) v.push('future_date');
  if (ctx.contract && !isActiveOn(ctx.contract, input.date)) v.push('outside_contract');
  if (!ctx.assignment || !isActiveOn(ctx.assignment, input.date)) v.push('outside_assignment');
  if (!ctx.allowedServiceIds.includes(input.serviceId)) v.push('service_not_allowed');
  if (ctx.absence) v.push('absence_day');
  if (
    ctx.timesheetHours !== null &&
    Math.round((ctx.otherReportsHours + input.hours) * 100) > Math.round(ctx.timesheetHours * 100)
  ) {
    v.push('exceeds_timesheet');
  }
  if (!input.katottgCode) v.push('location_missing');
  else if (
    ctx.areaCodes.length > 0 &&
    !(ctx.locationPath ?? []).some((code) => ctx.areaCodes.includes(code))
  ) {
    v.push('location_outside_area');
  }
  return v;
}

// ───────────── Правило 12.4.5: трирівневе погодження ─────────────

export type ApprovalLevel = 'center' | 'program' | 'hr';

/** Хто має погодити звіт у цьому статусі (null — погоджувати нічого). */
export function approvalLevel(status: ReportStatus): ApprovalLevel | null {
  switch (status) {
    case 'submitted':
      return 'center';
    case 'center_approved':
      return 'program';
    case 'program_approved':
      return 'hr';
    default:
      return null;
  }
}

export const NEXT_STATUS: Record<ApprovalLevel, ReportStatus> = {
  center: 'center_approved',
  program: 'program_approved',
  hr: 'approved',
};

export interface ApproverContext {
  userId: string;
  isHr: boolean;
  isAdmin: boolean;
  centerManagerId: string | null;
  programManagerId: string | null;
}

/**
 * Чи може користувач погодити звіт на поточному рівні. Якщо для рівня не призначено
 * керівника (немає керівника центру чи програми), рівень погоджує HR — щоб звіт не зависав.
 */
export function canApproveAt(level: ApprovalLevel, a: ApproverContext): boolean {
  if (a.isAdmin) return true;
  if (level === 'hr') return a.isHr;
  const manager = level === 'center' ? a.centerManagerId : a.programManagerId;
  if (manager) return manager === a.userId;
  return a.isHr;
}

export const EDITABLE_REPORT_STATUSES: ReportStatus[] = ['draft', 'returned'];

// ───────────── Схеми ─────────────

const hours = z
  .number()
  .min(0.25, 'Мінімум 0,25 год')
  .max(24)
  .transform((v) => Math.round(v * 100) / 100);

export const serviceReportFieldsSchema = z.object({
  employeeId: idSchema,
  /** Призначення (штатна одиниця), в межах якого надано послугу. */
  assignmentId: idSchema,
  serviceId: idSchema,
  date: isoDateSchema,
  katottgCode: z
    .string()
    .trim()
    .max(20)
    .nullish()
    .transform((v) => v || null),
  address: optionalText(500),
  description: z.string().trim().min(10, 'Опишіть, що зроблено (мінімум 10 символів)').max(5000),
  hours,
  quantity: z.number().int().min(1).max(1000).default(1),
  beneficiaries: z
    .number()
    .int()
    .min(0)
    .max(10_000)
    .nullish()
    .transform((v) => v ?? null),
});
export type ServiceReportFieldsInput = z.input<typeof serviceReportFieldsSchema>;

export const serviceReportUpdateSchema = serviceReportFieldsSchema
  .omit({ employeeId: true })
  .partial()
  .extend({ id: idSchema, expectedUpdatedAt: z.coerce.date().optional() });

export const serviceReportListSchema = listParamsSchema.extend({
  status: z.enum(REPORT_STATUSES).optional(),
  projectId: idSchema.optional(),
  centerId: idSchema.optional(),
  employeeId: idSchema.optional(),
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
  /** Тільки ті, що чекають погодження поточного користувача. */
  awaitingMe: z.boolean().optional(),
  /** Тільки з порушеннями. */
  withViolations: z.boolean().optional(),
});

export const reportIdsSchema = z.object({ ids: z.array(idSchema).min(1).max(500) });
export const reportReturnSchema = z.object({
  id: idSchema,
  comment: z.string().trim().min(1, 'Вкажіть причину').max(2000),
});
